<template>
  <section class="section-container">
    <h1 class="section-container-heading">
      <p
        v-for="(line, index) in lines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h1>
  </section>
  <section class="section-container">
    <div class="section-container-body fade-up-animation">
      <h3 class="heading-1">
        We are a team of passionate professionals turning ideas into powerful software solutions that shape the future.
      </h3>
      <p class="body-text">
        At Vortec, we don’t just build software — we build what’s next. Our team of engineers, designers, and innovators
        transforms bold ideas into powerful digital solutions that redefine how businesses grow and compete.
      </p>
      <p class="body-text">
        We believe technology should do more than keep up — it should lead. That’s why every project we deliver is
        crafted with precision, driven by passion, and fueled by innovation. From custom platforms to complex enterprise
        systems, we create solutions that don’t just meet expectations — they set new standards.
      </p>
      <p class="body-text">
        With Vortec, you don’t get a vendor. You get a partner committed to shaping the future, one line of code at a
        time.
      </p>
    </div>
  </section>

  <!-- <section class="section-container">
    <h2 class="section-container-heading">
      <p
        v-for="(line, index) in founderLines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h2>
    <div class="founders-container">
      <div class="founders-box fade-up-animation" v-for="founder in founders" :key="founder.name">
        <img :src="founder.image" :alt="`${founder.name} ${founder.surname}`" />
        <div class="founders-box-info">
          <h5 class="heading-4-list">{{ founder.surname }} {{ founder.name }}</h5>
          <p class="medium-text">{{ founder.role }}</p>
          <div class="founders-box-info-links">
            <nuxt-link :to="`mailto:${founder.mail}`" aria-label="Send Email" class="medium-text text-animation">
              <p class="text-animation-visible medium-text">Email</p>
              <p class="text-animation-hidden medium-text">Email</p>
            </nuxt-link>
            <nuxt-link
              :to="founder.linkedIn"
              target="_blank"
              rel="noopener"
              aria-label="Visit LinkedIn Profile"
              class="medium-text text-animation">
              <p class="text-animation-visible medium-text">LinkedIn</p>
              <p class="text-animation-hidden medium-text">LinkedIn</p>
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>
  </section> -->
  <section class="section-container section-container-wider section-container-double-gap" id="clients">
    <h2 class="section-container-heading">
      <p
        v-for="(line, index) in clientLines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h2>
    <Clients />
  </section>

  <section class="section-container section-container-wider section-container-double-gap">
    <h2 class="section-container-heading">
      <p
        v-for="(line, index) in linesCTA"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h2>
    <nuxt-link class="arrow-button arrow-button-bigger fade-up-animation" to="/contact" aria-label="Begin Imagining">
      <p class="heading-4-bold">Begin Imagining</p>
      <div class="arrow-button-icon" v-html="[icons.rightArrowIcon, icons.rightArrowIcon]"></div
    ></nuxt-link>
  </section>
</template>

<script setup>
import { useFadeUpObserver } from '@/composables/useFadeUpObserver';
import icons from '~/icons';

const lines = [
  { text: 'From Vision.', special: false },
  { text: 'To Reality.', special: true },
  { text: 'We build.', special: false },
  { text: "What's next.", special: true },
];

const linesCTA = [
  { text: 'Anything is', special: false },
  { text: 'possible.', special: true },
  { text: 'the question is,', special: false },
  { text: 'what can you ', special: true },
  { text: 'think of?', special: false },
];

const clientLines = [
  { text: 'Some of our', special: false },
  { text: 'Clients.', special: true },
];

const founderLines = [
  { text: 'Meet Our', special: false },
  { text: 'Founders.', special: true },
];

const founders = [
  {
    name: 'David',
    surname: 'Atanasoski',
    role: 'Head of Frontend Development',
    image: 'https://cdn.vortec.io/people/Atanasoski_David.webp',
    linkedIn: 'https://www.linkedin.com/in/davidatanasoskii/',
    mail: 'atanasoski.david@vortec.io',
  },
  {
    name: 'Mladen',
    surname: 'Aleksic',
    role: 'Head of Backend Development',
    image: 'https://cdn.vortec.io/people/Aleksic_Mladen.webp',
    linkedIn: 'https://www.linkedin.com/in/mladen-aleksic-b826771ab/',
    mail: 'aleksic.mladen@vortec.io',
  },
  {
    name: 'Kristijan',
    surname: 'Kitanov',
    role: 'Head of Operations & Sales',
    image: 'https://cdn.vortec.io/people/Kitanov_Kristijan.webp',
    linkedIn: 'https://www.linkedin.com/in/kitanovk/',
    mail: 'kitanov.kristijan@vortec.io',
  },
  {
    name: 'Teo',
    surname: 'Vojnovic',
    role: 'Head of Product Development',
    image: 'https://cdn.vortec.io/people/Vojnovic_Teo.webp',
    linkedIn: 'https://www.linkedin.com/in/teo-vojnovic-8a5b79292/',
    mail: 'vojnovic.teo@vortec.io',
  },
].sort((a, b) => a.surname.localeCompare(b.surname));

useFadeUpObserver();

useSeo({
  title: 'About | VORTEC',
  description:
    'Meet VORTEC — a team of engineers, designers and product builders turning vision into reality. Learn about our founders, our clients and how we work.',
  path: '/about',
  jsonLd: {
    '@type': 'AboutPage',
    name: 'About | VORTEC',
    description: 'Learn more about VORTEC and our team.',
    url: 'https://vortec.io/about',
    about: {
      '@type': 'Organization',
      name: 'VORTEC',
      url: 'https://vortec.io',
      employee: founders.map((person) => ({
        '@type': 'Person',
        name: `${person.name} ${person.surname}`,
        jobTitle: person.role,
        image: person.image,
        sameAs: person.linkedIn,
      })),
    },
  },
});
</script>
